import type { UnwindFormState } from "@/hooks/use-unwind-form";
import { CADC, USDC } from "@/config/tokens";
import { formatToken } from "@/lib/format";
import { formatHealthFactor } from "@/lib/loop/risk";

type Props = {
  readonly form: UnwindFormState;
};

type Tone = "ink" | "jade" | "rose";

type Row = {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: Tone;
};

const toneColor = (tone?: Tone): string => {
  if (tone === "jade") return "var(--accent-jade)";
  if (tone === "rose") return "var(--accent-rose)";
  return "var(--ink-1)";
};

const hfTone = (hf: number | undefined): Tone => {
  if (hf == null || !Number.isFinite(hf)) return "ink";
  if (hf < 1.1) return "rose";
  if (hf < 1.5) return "ink";
  return "jade";
};

const fmtLiq = (p: number | null | undefined): string => {
  if (p == null) return "—";
  return `$${p.toFixed(4)} / ${CADC.symbol}`;
};

const fmtPct = (curr: bigint | null, post: bigint): string => {
  if (curr == null || curr === 0n) return "—";
  const ratio = Number(post) / Number(curr);
  if (!Number.isFinite(ratio)) return "—";
  return `${(ratio * 100).toFixed(0)}% retained`;
};

export const CloseSummary = ({ form }: Props) => {
  const {
    venue,
    existingCollateralRaw,
    existingBorrowRaw,
    postCollateralRaw,
    postBorrowRaw,
    postRisk,
    fullClose,
  } = form;

  if (fullClose) {
    return (
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h3>Projected position</h3>
          <span
            className="font-mono text-[10px] tracking-[0.16em] uppercase"
            style={{ color: "var(--ink-4)" }}
          >
            Full close
          </span>
        </div>
        <p
          className="font-sans text-[13px]"
          style={{ color: "var(--ink-2)", lineHeight: 1.5 }}
        >
          This tx closes the entire {venue === "morpho" ? "Morpho" : "Ajna"} position —
          no collateral or debt will remain. Anything left over on the contract
          (CADC dust, USDC overshoot) returns to your wallet.
        </p>
      </section>
    );
  }

  const rows: Row[] = [
    {
      label: "Remaining collateral",
      value: `${formatToken(postCollateralRaw, CADC.decimals, 2)} ${CADC.symbol}`,
      hint: fmtPct(existingCollateralRaw, postCollateralRaw),
    },
    {
      label: "Remaining debt",
      value: `${formatToken(postBorrowRaw, USDC.decimals, 2)} ${USDC.symbol}`,
      hint: fmtPct(existingBorrowRaw, postBorrowRaw),
    },
    {
      label: "Health factor",
      value: postRisk ? formatHealthFactor(postRisk.healthFactor) : "—",
      tone: hfTone(postRisk?.healthFactor),
    },
    {
      label: "Liquidation price",
      value: fmtLiq(postRisk?.liquidationPrice),
    },
  ];

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h3>Projected position</h3>
      </div>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li
            key={r.label}
            className="flex items-baseline justify-between gap-6 pb-3"
            style={{
              borderBottom:
                i === rows.length - 1 ? "none" : "1px dashed var(--line-1)",
            }}
          >
            <span
              className="font-sans text-[12px] tracking-[0.04em]"
              style={{ color: "var(--ink-3)" }}
            >
              {r.label}
            </span>
            <span className="text-right">
              <span
                className="font-mono tabular text-[14px]"
                style={{ color: toneColor(r.tone), fontWeight: 400 }}
              >
                {r.value}
              </span>
              {r.hint && (
                <span
                  className="block font-sans text-[10px] tracking-[0.08em] uppercase mt-1"
                  style={{ color: "var(--ink-4)" }}
                >
                  {r.hint}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
